import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Store, Clock, ShoppingCart, Calendar } from 'lucide-react';
import axios from 'axios';
import { resolveImageUrl } from '../lib/imageUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatPrice = (v) => {
  if (v === null || v === undefined) return '-';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v);
};

const formatDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString('de-DE'); } catch { return null; }
};

export default function OfferLandingPage() {
  const { shopSlug, campaignId, productId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/public/shop/${shopSlug}/campaign/${campaignId}/product/${productId}`
        );
        setData(res.data);
      } catch (e) {
        setError('Angebot nicht gefunden oder abgelaufen');
      } finally {
        setLoading(false);
      }
    })();
  }, [shopSlug, campaignId, productId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !data?.product) {
    return (
      <div className="max-w-2xl mx-auto text-center py-20 px-4" data-testid="offer-error">
        <Store className="h-12 w-12 mx-auto text-gray-300 mb-4" />
        <h1 className="text-2xl font-bold mb-2">{error || 'Angebot nicht verfügbar'}</h1>
        <Button onClick={() => navigate(`/shop/${shopSlug}`)} className="mt-4">Zum Shop</Button>
      </div>
    );
  }

  const { shop, campaign, product } = data;
  const savings = product.regular_price && product.offer_price
    ? product.regular_price - product.offer_price
    : null;
  const savingsPct = product.regular_price && savings
    ? ((savings / product.regular_price) * 100).toFixed(0)
    : null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="offer-landing-page">
      {/* Shop header */}
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          {shop.logo_url ? (
            <img
              src={resolveImageUrl(shop.logo_url)}
              alt={shop.shop_name}
              className="h-10 w-10 rounded-full object-cover"
            />
          ) : (
            <Store className="h-8 w-8 text-gray-400" />
          )}
          <div>
            <h1 className="font-bold text-lg">{shop.shop_name}</h1>
            <button
              onClick={() => navigate(`/shop/${shopSlug}`)}
              className="text-xs text-gray-500 hover:text-primary"
            >
              → Zum Shop
            </button>
          </div>
        </div>
      </div>

      {/* Offer hero */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="bg-gray-100 flex items-center justify-center p-8">
              {product.image_url ? (
                <img
                  src={resolveImageUrl(product.image_url)}
                  alt={product.name}
                  className="max-h-80 object-contain"
                  data-testid="offer-product-image"
                />
              ) : (
                <Store className="h-24 w-24 text-gray-300" />
              )}
            </div>
            <div className="p-8 flex flex-col">
              {campaign?.offer_title && (
                <span className="inline-block bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded self-start mb-3">
                  {campaign.offer_title}
                </span>
              )}
              <h2 className="text-3xl font-bold mb-2" data-testid="offer-product-name">{product.name}</h2>
              {product.description && (
                <p className="text-gray-600 mb-4 line-clamp-3">{product.description}</p>
              )}
              <div className="mt-4 mb-6">
                {product.offer_price && product.offer_price !== product.regular_price ? (
                  <>
                    <div className="flex items-baseline gap-3">
                      <span className="text-4xl font-bold text-red-600" data-testid="offer-price">
                        {formatPrice(product.offer_price)}
                      </span>
                      <span className="text-lg text-gray-500 line-through">
                        {formatPrice(product.regular_price)}
                      </span>
                    </div>
                    {savingsPct && (
                      <p className="text-sm text-green-600 mt-1">
                        Sie sparen {formatPrice(savings)} ({savingsPct}%)
                      </p>
                    )}
                  </>
                ) : (
                  <span className="text-4xl font-bold" data-testid="offer-price">
                    {formatPrice(product.regular_price)}
                  </span>
                )}
              </div>
              {product.offer_end_at && (
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                  <Clock className="h-4 w-4" />
                  Gültig bis {formatDate(product.offer_end_at)}
                </div>
              )}
              {campaign?.offer_message && (
                <p className="text-sm text-gray-700 mb-6 whitespace-pre-wrap">
                  {campaign.offer_message
                    .replace(/{{shop_name}}/g, shop.shop_name)
                    .replace(/{{product_name}}/g, product.name)
                    .replace(/{{offer_price}}/g, formatPrice(product.offer_price || product.regular_price))
                    .replace(/{{offer_link}}/g, '')}
                </p>
              )}
              <div className="flex flex-col gap-2 mt-auto">
                <Button
                  size="lg"
                  onClick={() => navigate(`/shop/${shopSlug}`)}
                  data-testid="offer-shop-btn"
                >
                  <ShoppingCart className="h-4 w-4 me-2" />
                  Zum Shop &amp; bestellen
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate(`/shop/${shopSlug}/booking`)}
                  data-testid="offer-booking-btn"
                >
                  <Calendar className="h-4 w-4 me-2" />
                  Termin buchen
                </Button>
              </div>
            </div>
          </div>
        </div>

        <p className="text-xs text-gray-400 text-center mt-6">
          Sie erhalten dieses Angebot von {shop.shop_name}. Alle Preise inkl. MwSt.
        </p>
      </div>
    </div>
  );
}
